import React, { useState } from 'react';
import { useCountUp } from '../../../hook/useCountUp';
import { useInView } from '../../../hook/useInView';
import style from './NutritionStat.module.css';

// ───────── SVG 도넛 차트 ─────────────────────────────────────────
const SIZE = 74;
const STROKE = 6;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * @param {string} label
 * @param {number} percent  표시용 % (110% 등 100 초과 가능)
 * @param {number} current
 * @param {number} goal
 * @param {string} unit
 * @param {boolean} animate 애니메이션 트리거 여부
 */

const formatNutrition = (value, unit) => {
    const num = Number(value ?? 0);

    if (unit === 'kcal' || unit === 'mg') {
        return Math.round(num).toLocaleString('ko-KR');
    }

    return (Math.round(num * 10) / 10).toLocaleString('ko-KR', {
        maximumFractionDigits: 1,
    });
};

const NutritionDonut = ({ label, percent, current, goal, unit, animate }) => {
    const isWarn = percent > 100;

    // 차트 채우기 비율 (최대 100)
    const fillTarget = Math.min(percent, 100);

    // 애니메이션 값들
    const fillAnim    = useCountUp(fillTarget, { duration: 1200, enabled: animate });
    const percentAnim = useCountUp(percent,    { duration: 1200, enabled: animate });
    const currentAnim = useCountUp(current,    { duration: 1200, enabled: animate });

    const dashOffset = CIRCUMFERENCE - (fillAnim / 100) * CIRCUMFERENCE;
    const colorClass = isWarn ? style.warn : style.good;
    const strokeColor = isWarn ? 'var(--color-important)' : '#2F91FA';

    return (
        <div className={style.donutItem}>
            <div className={style.donutWrap} aria-hidden="true">
                <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                    {/* 배경 트랙 */}
                    <circle
                        cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
                        fill="none"
                        stroke="#D5E9FE"
                        strokeWidth={STROKE}
                    />
                    {/* 진행 stroke */}
                    <circle
                        cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
                        fill="none"
                        stroke={strokeColor}
                        strokeWidth={STROKE}
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={dashOffset}
                        strokeLinecap="square"
                        transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
                    />
                </svg>
                <div className={`${style.donutPercent} ${colorClass}`}>
                    {Math.round(percentAnim)}<em>%</em>
                </div>
            </div>
            <p className={style.donutLabel}>{label}</p>
            <p className={`${style.donutValue} ${colorClass}`}>
                {/* {Math.round(currentAnim).toLocaleString()}<span>{unit}</span> */}

                {/* 자리수 수정 작업  */}
                {formatNutrition(currentAnim, unit)}<span>{unit}</span>               
            </p>
            <p className={style.donutGoal}>{goal.toLocaleString()}{unit}</p>
        </div>
    );
};

// ───────── 진행바 ────────────────────────────────────────────────
const NutritionBar = ({ label, current, goal, unit, animate }) => {
    const percent = Math.min(Math.round((current / goal) * 100), 100);
    const isWarn = current > goal;

    const currentAnim = useCountUp(current, { duration: 1200, enabled: animate });
    const percentAnim = useCountUp(percent, { duration: 1200, enabled: animate });

    return (
        <div className={style.barItem}>
            <div className={style.barHead}>
                <span>{label}</span>
                <span className={style.barValue}>
                    <em className={isWarn ? style.warn : style.good}>
                        {/* {Math.round(currentAnim)}{unit} */}

                        {/* 자리수 수정  */}
                        {formatNutrition(currentAnim, unit)}{unit}
                    </em>
                    <span className={style.barGoal}>/{goal}{unit}</span>
                </span>
            </div>
            <div className={style.barTrack}>
                <div
                    className={`${style.barFill} ${isWarn ? style.warnBg : style.goodBg}`}
                    style={{ width: `${percentAnim}%` }}
                />
            </div>
        </div>
    );
};

// ───────── 경고 박스 ─────────────────────────────────────────────
const WarnNotice = ({ message }) => (
    <div className={style.warnNotice} role="alert">
        <span className={style.warnIcon} aria-hidden="true">!</span>
        {message}
    </div>
);

// ───────── 메인 (조립) ────────────────────────────────────────────
/**
 * @param {object} data
 * @param {{current,goal,percent,unit}} data.kcal
 * @param {{current,goal,percent,unit}} data.sodium
 * @param {{current,goal,percent,unit}} data.protein
 * @param {{current,goal,unit}} data.carb
 * @param {{current,goal,unit}} data.fat
 */

const DEFAULT_DATA = {
    energy:       { value: 0, goal: 0, unit: 'kcal' },
    sodium:       { value: 0, goal: 0, unit: 'mg' },
    protein:      { value: 0, goal: 0, unit: 'g' },
    carbohydrate: { value: 0, goal: 0, unit: 'g' },
    fat:          { value: 0, goal: 0, unit: 'g' }
};

const getPercent = (value, goal) => {
    if (!goal || goal <= 0) return 0;

    return Math.round((value / goal) * 100);
};

const NutritionStat = ({ data, showBars = true, showWarnings = true , title, addBtn, btnTxt, onBtnClick }) => {
    const [ref, inView] = useInView({ threshold: 0.2, once: true });

    const nutritionData = {
        energy: {
            ...DEFAULT_DATA.energy,
            ...data.energy
        },
        sodium: {
            ...DEFAULT_DATA.sodium,
            ...data.sodium
        },
        protein: {
            ...DEFAULT_DATA.protein,
            ...data.protein
        },
        carbohydrate: {
            ...DEFAULT_DATA.carbohydrate,
            ...data.carbohydrate
        },
        fat: {
            ...DEFAULT_DATA.fat,
            ...data.fat
        }
    };


    const energyPer = getPercent(
        nutritionData.energy.value,
        nutritionData.energy.goal
    );

    const sodiumPer = getPercent(
        nutritionData.sodium.value,
        nutritionData.sodium.goal
    );

    const proteinPer = getPercent(
        nutritionData.protein.value,
        nutritionData.protein.goal
    );

    const carbohydratePer = getPercent(
        nutritionData.carbohydrate.value,
        nutritionData.carbohydrate.goal
    );

    const fatPer = getPercent(
        nutritionData.fat.value,
        nutritionData.fat.goal
    );

    let warning = '';

    if ( nutritionData.sodium.goal > 0 && nutritionData.sodium.value > nutritionData.sodium.goal ) {
        warning = '나트륨 일일 권장량을 초과했습니다.';
    } else if (nutritionData.energy.goal > 0 && nutritionData.energy.value > nutritionData.energy.goal) {
        warning = '칼로리 일일 권장량을 초과했습니다.';
    } else if (nutritionData.protein.goal > 0 && nutritionData.protein.value > nutritionData.protein.goal) {
        warning = '단백질 일일 권장량을 초과했습니다.';
    }

    return (
        <div className={style.box} ref={ref}>
            {title && <h2 className={style.title}>{title}</h2>}
            <div className={style.donutRow}>
                <NutritionDonut label="칼로리" percent={energyPer} current={nutritionData.energy.value} goal={nutritionData.energy.goal} unit={nutritionData.energy.unit} animate={inView} />
                <NutritionDonut label="나트륨" percent={sodiumPer} current={nutritionData.sodium.value} goal={nutritionData.sodium.goal} unit={nutritionData.sodium.unit} animate={inView} />
                <NutritionDonut label="단백질" percent={proteinPer} current={nutritionData.protein.value} goal={nutritionData.protein.goal} unit={nutritionData.protein.unit} animate={inView} />
            </div>

            {showBars && (nutritionData.carbohydrate || data.fat) && (
                <div className={style.barList}>
                    {nutritionData.carbohydrate && <NutritionBar label="탄수화물" current={nutritionData.carbohydrate.value} goal={nutritionData.carbohydrate.goal} unit={nutritionData.carbohydrate.unit} animate={inView} />}
                    {nutritionData.fat &&  <NutritionBar label="지방" current={nutritionData.fat.value} goal={nutritionData.fat.goal} unit={nutritionData.fat.unit} animate={inView} />}
                </div>
            )}

            {showWarnings && warning &&
                <WarnNotice key={warning} message={warning} />
            }

            {addBtn && 
                <button type="button" className="btn m-t-08" data-btn-type="fill-main" data-btn-size='md' data-btn-width='full' onClick={onBtnClick}><span>{btnTxt}</span></button>
            }
        </div>
    );
};

export default NutritionStat;